import { GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { FC, MutableRefObject } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { TrendUp01Icon } from '@/components/icons/TrendUp01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { useReportError } from '@/hooks/useReportError';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { ClientValuePerformanceDashboardSummary } from './ClientValuePerformanceDashboard.components';
import {
  mapAdvisorClientToValueDashboardRows,
  Row,
} from './ClientValuePerformanceDashboard.utils';
import { ClientValuePerformanceTable } from './ClientValuePerformanceTable';
import { useClientValuePerformanceDashboardQuery } from './graphql/ClientValuePerformanceDashboard.generated';

export interface ClientValuePerformanceDashboardContentProps {
  householdId: string;
  gridApiRef?: MutableRefObject<GridApiPro>;
  rows: Row[];
  loading?: boolean;
}
export function ClientValuePerformanceDashboardContent({
  householdId,
  gridApiRef,
  rows,
  loading = false,
}: ClientValuePerformanceDashboardContentProps) {
  return (
    <DataTableCard>
      <ClientValuePerformanceTable
        rows={rows}
        loading={loading}
        householdId={householdId}
        apiRef={gridApiRef}
      />
    </DataTableCard>
  );
}

interface ClientValuePerformanceDashboardProps {
  householdId: string;
}

export const ClientValuePerformanceDashboard: FC<
  ClientValuePerformanceDashboardProps
> = ({ householdId }) => {
  const gridApiRef = useGridApiRef();

  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const { data, loading: loadingFromNetwork } =
    useClientValuePerformanceDashboardQuery({
      variables: { householdId },
      fetchPolicy: 'cache-and-network',
      onError: (error) => {
        reportError('Encountered an issue when loading report', error);
        showFeedback('Encountered an error when loading report');
      },
    });

  const household = getNodes(data?.households)?.[0] || null;
  const clientPerformanceReport = household?.clientPerformanceReport;
  const { rows, familyWealthTransferred, charityWealthTransferred } =
    mapAdvisorClientToValueDashboardRows(household);

  const loading = loadingFromNetwork && !data;

  // The mapper always includes a "Family giving" header row, so `rows`
  // is never truly empty. The dashboard is blank when only the header
  // exists (length 1) and both totals are zero.
  const hasNoData =
    rows.length <= 1 &&
    familyWealthTransferred.isZero() &&
    charityWealthTransferred.isZero();

  if (hasNoData && !loading) {
    return (
      <SmallTableEmptyState
        fill="viewport"
        icon={<TrendUp01Icon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="No value creation data"
        description="Value creation data will appear once strategies such as GRATs, gifts, or charitable trusts are implemented"
      />
    );
  }

  return (
    <SubpageLayout heading={undefined}>
      <ClientValuePerformanceDashboardSummary
        familyGivingWealthTransferred={familyWealthTransferred}
        charityWealthTransferred={charityWealthTransferred}
        clientPerformanceReport={clientPerformanceReport}
      />
      <ClientValuePerformanceDashboardContent
        rows={rows}
        householdId={householdId}
        gridApiRef={gridApiRef}
        loading={loading}
      />
    </SubpageLayout>
  );
};
